import { toast } from "react-toastify";

import {
  ImportAgenticLogicRuleVersionDraftRequestPathParamsType,
  ImportAgenticLogicRuleVersionDraftResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

const importAgenticLogicRuleVersionDraft = async (
  payload: ApiRequest<
    unknown,
    ImportAgenticLogicRuleVersionDraftRequestPathParamsType
  >
) => {
  const resp = await api.post<
    ImportAgenticLogicRuleVersionDraftResponseBodyType,
    unknown
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/versions/${payload.pathParams.version}/import-draft`,
    {}
  );
  return resp.data;
};

export const useImportAgenticLogicRuleVersionDraft = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ImportAgenticLogicRuleVersionDraftResponseBodyType,
    Error,
    ApiRequest<
      unknown,
      ImportAgenticLogicRuleVersionDraftRequestPathParamsType
    >,
    unknown
  >({
    mutationFn: async (data) => importAgenticLogicRuleVersionDraft(data),
    // The draft key is NOT invalidated here: the port seeds it with the
    // response so the editor reseeds from the imported source in one paint,
    // and an invalidation would race that seed with a refetch.
    onSuccess: async (_data, payload) => {
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.versions(payload.pathParams.ruleId),
      });
      // The imported version brings its own generated suite.
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.generatedTests(payload.pathParams.ruleId),
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to import version${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
